import { callApi } from '../utils';
import { routes } from '../../lib/routes';
import {
    GrammarIntentShort,
    GrammarIntent,
    GrammarIntentTestResult,
    GrammarIntentEditableFields,
    GrammarValidationErrorPayload,
    GrammarIntentFlags,
} from '../../model/grammar';
import { VerstehenIndexes } from '../../blocks/GrammarPageVerstehen/GrammarPageVerstehen';
import { Channel } from 'client/model/skill';

interface GetVerstehenParams {
    skillId: string;
    intentSettings: GrammarIntentEditableFields;
    entities: string;
}

interface Log {
    index_payload: string;
    idx: number;
}
export interface Logs {
    search_result: Log[];
}

const NUMBER_OF_LOGS = 100;

export const getVerstehenLogs = async (
    { skillId, intentSettings, entities }: GetVerstehenParams,
    indexStatus: VerstehenIndexes,
    channel: Channel,
): Promise<Logs> => {
    const route = `/apps/${skillId}/verstehen`;
    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(route),
        queryParams: { channel },
        data: {
            ...intentSettings,
            index_name: indexStatus,
            numSamples: NUMBER_OF_LOGS,
            ...(indexStatus === 'Granet' && { entities }),
        },
    });
};

interface GetGrammarIntentListParams {
    skillId: string;
    channel: Channel;
    isDraft?: boolean;
}

export const getGrammarIntentList = async ({ skillId, channel, isDraft = false }: GetGrammarIntentListParams) => {
    const route = isDraft ? `/apps/${skillId}/intents/drafts` : `/apps/${skillId}/intents`;

    return callApi<GrammarIntentShort[]>({
        method: 'GET',
        url: routes.app.appStoreApi(route),
        queryParams: { channel: channel },
    });
};

interface GetGrammarIntentParams {
    skillId: string;
    channel: Channel;
    intentId: string;
    isDraft?: boolean;
}

export const getGrammarIntent = async ({ skillId, channel, intentId, isDraft = false }: GetGrammarIntentParams) => {
    const route = isDraft ? `/apps/${skillId}/intents/drafts/${intentId}` : `/apps/${skillId}/intents/${intentId}`;

    return callApi<GrammarIntent>({
        method: 'GET',
        url: routes.app.appStoreApi(route),
        queryParams: { channel: channel },
    });
};

interface CreateGrammarIntentParams {
    skillId: string;
    channel: Channel;
}

export const createGrammarIntent = async ({ skillId, channel }: CreateGrammarIntentParams) => {
    return callApi<GrammarIntent>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillId}/intents/draft`),
        data: {},
        queryParams: { channel: channel },
    });
};

interface UpdateGrammarIntentParams {
    skillId: string;
    channel: Channel;
    intentSettings: GrammarIntentEditableFields;
    intentId: string;
}

interface UpdateGrammarIntentResponse {
    intent: GrammarIntent;
    validationError: GrammarValidationErrorPayload | null;
}

export const updateGrammarIntent = ({ skillId, channel, intentSettings, intentId }: UpdateGrammarIntentParams) => {
    return callApi<UpdateGrammarIntentResponse>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`/apps/${skillId}/intents/${intentId}/draft`),
        data: intentSettings,
        queryParams: { channel: channel },
    });
};

interface DeleteGrammarIntentParams {
    skillId: string;
    intentId: string;
}

export const deleteGrammarIntent = ({ skillId, intentId }: DeleteGrammarIntentParams) => {
    return callApi({
        method: 'DELETE',
        url: routes.app.appStoreApi(`/apps/${skillId}/intents/${intentId}/draft`),
    });
};

interface RunGrammarIntentTestsParams {
    skillId: string;
    intentId: string;
}

export const runGrammarIntentTests = ({ skillId, intentId }: RunGrammarIntentTestsParams, channel: Channel) => {
    return callApi<GrammarIntentTestResult>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillId}/intents/${intentId}/draft/actions/test`),
        queryParams: { channel: channel },
    });
};

interface UpdateCustomEntitiesParams {
    sourceText: string;
    skillId: string;
}

export const updateCustomEntities = ({ sourceText, skillId }: UpdateCustomEntitiesParams, channel: Channel) => {
    return callApi({
        method: 'PUT',
        url: routes.app.appStoreApi(`/apps/${skillId}/drafts/entities`),
        data: { sourceText },
        queryParams: { channel },
    });
};

export const patchIntentFlags = (
    intentId: string,
    skillId: string,
    channel: Channel,
    flags: GrammarIntentFlags,
    isDraft: boolean = true,
) => {
    const path = isDraft
        ? `/apps/${skillId}/intents/${intentId}/draft/flags`
        : `/apps/${skillId}/intents/${intentId}/flags`;
    return callApi({
        method: 'PATCH',
        url: routes.app.appStoreApi(path),
        data: flags,
        queryParams: { channel },
    });
};
